import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdExpandMore, MdExpandLess, MdLocationOn, MdCalendarToday, MdEvent, MdFilterList, MdCloudUpload, MdCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import type { CombinedEvent } from '../upcomingEvents/types';
import type { RootState } from '../../redux/store';
import { uploadEventsRequest } from '../../redux/slices/eventUploadSlice';
import { parseExcelFile, validateEventData } from '../../utils/excelParser';

interface EventsListProps {
  events: CombinedEvent[];
  onSelectEvent: (event: CombinedEvent) => void;
  selectedEvent?: CombinedEvent | null;
}

const EventsList: React.FC<EventsListProps> = ({ events, onSelectEvent, selectedEvent }) => {
  const dispatch = useDispatch();
  const { loading: uploadLoading, error: uploadError, success: uploadSuccess } = useSelector((state: RootState) => state.eventUpload);
  
  // Get current month name
  const getCurrentMonth = () => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[new Date().getMonth()];
  };
  
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showUpcomingOnly, setShowUpcomingOnly] = useState<boolean>(true); // Default to showing only upcoming events
  const [showUploadMessage, setShowUploadMessage] = useState<boolean>(false);
  const [uploadMessage, setUploadMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  const monthDropdownRef = useRef<HTMLDivElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Get unique event types/categories
  const eventCategories = useMemo(() => {
    const cats = new Set<string>();
    events.forEach(event => {
      if (event.type) cats.add(event.type);
    });
    return ['All Categories', ...Array.from(cats).sort()];
  }, [events]);

  const getEventsByMonth = (month: string) => {
    const monthNum = (months.indexOf(month) + 1).toString().padStart(2, '0');
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
    
    return events.filter(event => {
      const eventDate = new Date(event.date);
      const isUpcoming = eventDate >= today;
      const matchesMonth = event.date.includes(`-${monthNum}-`);
      const matchesCategory = selectedCategories.length === 0 || 
                             selectedCategories.includes('All Categories') ||
                             selectedCategories.includes(event.type);
      
      // Apply upcoming filter only if showUpcomingOnly is true
      const matchesTimeFilter = showUpcomingOnly ? isUpcoming : true;
      
      return matchesTimeFilter && matchesMonth && matchesCategory;
    });
  };

  const filteredEvents = getEventsByMonth(selectedMonth);

  const toggleCategory = (category: string) => {
    if (category === 'All Categories') {
      setSelectedCategories([]);
    } else {
      if (selectedCategories.includes(category)) {
        setSelectedCategories(selectedCategories.filter(c => c !== category));
      } else {
        setSelectedCategories(selectedCategories.filter(c => c !== 'All Categories').concat(category));
      }
    }
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      alert('Please upload an Excel file (.xlsx, .xls, or .csv)');
      e.target.value = '';
      return;
    }

    try {
      // Parse the Excel file
      const parsedEvents = await parseExcelFile(file);

      // Validate
      const validation = validateEventData(parsedEvents);
      if (!validation.isValid) {
        alert(`Validation errors:\n${validation.errors.join('\n')}`);
        e.target.value = '';
        return;
      }

      // Dispatch upload action
      dispatch(uploadEventsRequest(parsedEvents));
      
    } catch (error) {
      console.error('Error parsing file:', error);
      alert(`Failed to parse file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      e.target.value = '';
    }
  };

  // Show success/error messages
  useEffect(() => {
    if (uploadSuccess) {
      setUploadMessage({ type: 'success', text: 'Events uploaded successfully!' });
      setShowUploadMessage(true);
      setTimeout(() => setShowUploadMessage(false), 3000);
    }
  }, [uploadSuccess]);

  useEffect(() => {
    if (uploadError) {
      setUploadMessage({ type: 'error', text: uploadError });
      setShowUploadMessage(true);
      setTimeout(() => setShowUploadMessage(false), 5000);
    }
  }, [uploadError]);

  // Helper to get a unique identifier for each event
  const getEventId = (event: CombinedEvent) => {
    return event.id || `${event.name}-${event.date}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTypeBadgeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      'National': 'bg-blue-500/20 text-blue-400',
      'Commercial': 'bg-purple-500/20 text-purple-400',
      'Religious': 'bg-green-500/20 text-green-400',
      'Cultural/Religious': 'bg-orange-500/20 text-orange-400',
      'Sports': 'bg-red-500/20 text-red-400',
      'Music': 'bg-pink-500/20 text-pink-400',
      'Festival': 'bg-yellow-500/20 text-yellow-400'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400';
  };

  const getCategoryIcon = (category: CombinedEvent['category']) => {
    switch(category) {
      case 'Holiday':
        return <MdCalendarToday className="text-blue-400 text-xl flex-shrink-0 mt-1" />;
      case 'Local Event':
        return <MdLocationOn className="text-green-400 text-xl flex-shrink-0 mt-1" />;
      case 'Event Master':
        return <MdEvent className="text-purple-400 text-xl flex-shrink-0 mt-1" />;
      default:
        return <MdCalendarToday className="text-blue-400 text-xl flex-shrink-0 mt-1" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="flex-1 flex overflow-hidden"
    >
      {/* Left Side - Events Image */}
      <div className="w-1/2 flex items-center justify-center p-8 bord border-gray-700">
        <img 
          src="/assets/images/drawer/calender.png" 
          alt="Events"
          className="w-full h-auto max-w-lg object-contain"
        />
      </div>

      {/* Right Side - Events List */}
      <div className="w-1/2 flex flex-col overflow-hidden">
        {/* Header with Upload Button */}
        <div className="px-8 py-6 flex-shrink-0">
          {/* Month Dropdown and Filter Row */}
          <div className="flex items-center gap-3 relative">
          <div ref={monthDropdownRef} className="flex-1 relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex-1 px-3 py-2 bg-[#212121] border border-gray-700 rounded-xl text-white text-sm focus:outline-none hover:border-gray-600 transition-colors flex items-center justify-between"
          >
            <span className="text-sm">{selectedMonth}</span>
            <motion.div
              animate={{ rotate: isDropdownOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <MdExpandMore className="text-xl" />
            </motion.div>
          </button>

          <AnimatePresence>
            {isDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full left-0 right-0 mt-2 bg-[#212121] border border-gray-700 rounded-xl overflow-hidden z-10 max-h-60 overflow-y-auto"
              >
                {months.map((month) => (
                  <button
                    key={month}
                    onClick={() => {
                      setSelectedMonth(month);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-700/50 transition-colors ${
                      selectedMonth === month ? 'bg-[#EE4423] text-white' : 'text-gray-300'
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
          </div>

          {/* Filter Button */}
          <div ref={filterDropdownRef} className="relative flex items-center gap-3">
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className={`p-3 rounded-xl transition-colors flex-shrink-0 ${
              isFilterOpen || selectedCategories.length > 0 ? 'bg-[#EE4423] text-white' : 'bg-[#212121] border border-gray-700 text-white hover:border-gray-600'
            }`}
          >
            <MdFilterList className="text-2xl" />
          </button>

          {/* Upload Button with Message */}
          <div className="relative">
            <button
              onClick={handleUpload}
              disabled={uploadLoading}
              className={`p-3 rounded-xl transition-colors flex-shrink-0 ${
                uploadLoading 
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                  : 'bg-[#212121] border border-gray-700 text-white hover:border-gray-600'
              }`}
            >
              {uploadLoading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              ) : (
                <MdCloudUpload className="text-2xl" />
              )}
            </button>
            
            {/* Upload Message */}
            <AnimatePresence>
              {showUploadMessage && uploadMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className={`absolute top-full right-0 mt-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap z-20 ${
                    uploadMessage.type === 'success' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-red-500 text-white'
                  }`}
                >
                  {uploadMessage.text}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            className="hidden"
          />

          {/* Category Filter Dropdown */}
          <AnimatePresence>
            {isFilterOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full right-0 mt-2 w-64 bg-[#212121] border border-gray-700 rounded-xl overflow-hidden z-10 max-h-80 overflow-y-auto"
              >
                {/* Show Upcoming Only Toggle */}
                <button
                  onClick={() => setShowUpcomingOnly(!showUpcomingOnly)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-700/50 transition-colors border-b border-gray-800 text-gray-300 flex items-center justify-between bg-gray-800/50"
                >
                  <span className="text-sm font-medium">
                    {showUpcomingOnly ? 'Upcoming Events Only' : 'All Events'}
                  </span>
                  {showUpcomingOnly && (
                    <MdCircle className="text-[#EE4423] text-xs" />
                  )}
                </button>
                
                {/* Category Filters */}
                {eventCategories.map((category) => {
                  const isSelected = (category === 'All Categories' && selectedCategories.length === 0) ||
                                   selectedCategories.includes(category);
                  return (
                    <button
                      key={category}
                      onClick={() => toggleCategory(category)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-700/50 transition-colors border-b border-gray-800 last:border-b-0 text-gray-300 flex items-center justify-between"
                    >
                      <span className="text-sm">{category}</span>
                      {isSelected && (
                        <MdCircle className="text-[#EE4423] text-xs" />
                      )}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
          </div>
        </div>
      </div>

        {/* Events List - Fixed height with scroll */}
        <div className="flex-1 overflow-y-auto px-8 space-y-3 pb-6 min-h-0">
        {filteredEvents.map((event) => {
          const eventId = getEventId(event);
          const isSelected = selectedEvent && getEventId(selectedEvent) === eventId;
          return (
            <motion.div
              key={eventId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => onSelectEvent(event)}
              className={`cursor-pointer bg-[#212121] rounded-xl overflow-hidden transition-all ${
                isSelected 
                  ? 'border-1 border-[#EE4423]' 
                  : 'border border-gray-700 hover:border-gray-600'
              }`}
            >
              {/* Event Header */}
              <div 
                className="p-3"
                onClick={() => {
                  setExpandedEventId(expandedEventId === eventId ? null : eventId);
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="">
                    <div className='flex items-start gap-1 flex-1'>
                    {/* {getCategoryIcon(event.category)} */}
                    <div className="flex-1">
                      <h3 className="text-white font-medium text-sm mb-1">
                        {event.name}
                      </h3>
                      </div>
                    </div>

                    <div className="flex-1">
                      <p className="text-gray-400 text-[10px]">
                        {formatDate(event.date)}
                      </p>
                    </div>

                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-medium ${getTypeBadgeColor(event.type)}`}>
                      {event.type}
                    </span>
                    {/* <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-gray-700 text-gray-300">
                      {event.category}
                    </span> */}
                    {expandedEventId === eventId ? (
                      <MdExpandLess className="text-white text-xl" />
                    ) : (
                      <MdExpandMore className="text-white text-xl" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              <AnimatePresence>
                {expandedEventId === eventId && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="border-t border-gray-700 overflow-hidden"
                  >
                    <div className="p-4 space-y-3">
                      {/* Consumer Behavior (for holidays) */}
                      {event.consumer_behavior && (
                        <div>
                          <p className="text-gray-300 text-xs font-medium mb-1">Consumer Behavior:</p>
                          <p className="text-gray-300 text-[10px]">
                            {event.consumer_behavior}
                          </p>
                        </div>
                      )}

                      {/* Marketing Angle (for holidays) */}
                      {event.marketing_angle && (
                        <div>
                          <p className="text-gray-300 text-xs font-medium mb-1">Marketing Angle:</p>
                          <p className="text-gray-300 text-[10px]">
                            {event.marketing_angle}
                          </p>
                        </div>
                      )} 

                      {/* Location (all events) */}
                      {event.location && (
                        <div>
                          <p className="text-gray-300 text-xs font-medium mb-1">Location:</p>
                          <p className="text-gray-300 text-[10px] flex items-center gap-1">
                            <MdLocationOn className="text-xs" />
                            {event.location}
                          </p>
                        </div>
                      )}

                      {/* Venue (for local events) */}
                      {event.venue && (
                        <div>
                          <p className="text-gray-300 text-xs font-medium mb-1">Venue:</p>
                          <p className="text-gray-300 text-[10px]">
                            {event.venue}
                          </p>
                        </div>
                      )}

                      {/* State */}
                      {event.state && (
                        <div>
                          <p className="text-gray-300 text-xs font-medium mb-1">State:</p>
                          <p className="text-gray-300 text-[10px]">
                            {event.state}
                          </p>
                        </div>
                      )}

                      {/* Source (for event master) */}
                      {event.source && (
                        <div>
                          <p className="text-gray-300 text-xs font-medium mb-1">Source:</p>
                          <p className="text-gray-300 text-[10px]">
                            {event.source}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}

          {filteredEvents.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              No events found for {selectedMonth}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default EventsList;
